import React from 'react';
import type { ModuleDefinition } from './moduleRegistry';
import type { DirectorshipBoard, DirectorDeepProfile, ManagementProfile, ReportData, ShareholdingRow } from '../tokens/types';
import { PdfSection, PdfChip } from '../primitives/PdfPrimitives';
import { ScreenSection, ScreenChip } from '../primitives/ScreenPrimitives';
import { View, Text } from '@react-pdf/renderer';
import { colors, coverFonts, overviewColors, ptToPx } from '../tokens/designTokens';
import { tenureFrom, fmtDate, fmtMonthYear, truncate } from './formatUtils';

// ════════════════════════════════════════════════════════════════
//                        D E S I G N   T O K E N S
// ════════════════════════════════════════════════════════════════
//
// Local card-level tokens for the Management Profile view. Values are in PDF
// points (1pt = 4/3 px); the screen renderer scales via `ptToPx()` so both
// stay in sync. If any of these need to be reused elsewhere, promote to
// `designTokens.ts`. For now they live here to keep the module self-contained.

const mgmtDesign = {
    // Card grid
    gridGap: 14,
    // Constant card height — header (~38pt) + headerToRows (14pt) + 5 × 19pt rows
    // (95pt) + card padding (24pt) ≈ 171pt. Anything less and the bottom row's
    // descender clips against the card border.
    cardMinHeight: 168,
    cardRadius: 4,
    cardPad: 12,
    cardBorder: 0.5,

    // Header (avatar + name + chip)
    headerGap: 9,
    avatarSize: 28,
    avatarRadius: 14,
    avatarFontSize: 9,

    nameSize: 9.5,
    subtitleSize: 7,
    subtitleGap: 2,

    // Active / Inactive chip — styled identically to the news sentiment chip
    // (same three-part centering pattern from skill rule 9).
    chipPadX: 5.5,
    chipPadTop: 2,
    chipPadBottom: 3,
    chipRadius: 1.5,
    chipFontSize: 7,
    chipTrackingEm: 0.06,

    // Detail rows. Tightened font sizes (label 7, value 7.5) and bumped row
    // height 16 → 19 for breathing room between items per Figma node 32:99627.
    // Values render in `inkSoft` (lighter than ink) so the LABEL/VALUE pair
    // reads as eyebrow + body rather than two equal-weight lines.
    headerToRows: 14,
    rowHeight: 19,
    rowLabelSize: 7,
    rowValueSize: 7.5,
    rowDividerWidth: 0.5,
    rowSubtleColor: '#EDEEF1', // softer hairline (was #E6E7EA — too prominent)

    // Employees block
    employeesGap: 22,
    employeesHeading: 10,
    employeesNumber: 26,
    employeesAsOfSize: 7.5,
    employeesAsOfGap: 4,
    employeesChartGap: 12,
    employeesCaption: 7.5,
} as const;

const SECTION_HEADING = {
    fontSize: 11,
    color: colors.ink00,
    marginBottom: 8,
} as const;

// ════════════════════════════════════════════════════════════════
//                        S H A R E D   H E L P E R S
// ════════════════════════════════════════════════════════════════

const DASH = '—';

const initials = (name: string): string =>
    (name || '')
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((p) => p[0]?.toUpperCase())
        .join('') || DASH;

const tenureMonths = (begin?: number): number | undefined => {
    if (!begin) return undefined;
    const now = Date.now();
    const ms = now - begin;
    if (ms <= 0) return 0;
    return Math.floor(ms / (1000 * 60 * 60 * 24 * 30.44));
};

const formatTenureBadge = (months?: number): string | undefined => {
    if (months == null) return undefined;
    if (months < 1) return '<1mo';
    if (months < 12) return `${months}m`;
    const yrs = Math.floor(months / 12);
    const rem = months % 12;
    return rem ? `${yrs}y ${rem}m` : `${yrs}y`;
};

// "—" if value is null/undefined/empty/placeholder string. Anything else is rendered as-is.
const orDash = (v: unknown): string => {
    if (v == null) return DASH;
    const s = String(v).trim();
    if (!s || /^(n\/?a|none|nil|null|undefined|—|-+)$/i.test(s)) return DASH;
    return s;
};

type DirectorRow = { label: string; value: string };

const buildDirectorRows = (p: ManagementProfile): DirectorRow[] => {
    const months = tenureMonths(p.tenureBeginDate);
    const tenure = formatTenureBadge(months) ?? tenureFrom(p.tenureBeginDate);
    return [
        { label: 'Age', value: orDash(p.age) },
        { label: 'Tenure', value: orDash(tenure) },
        { label: 'DIN', value: orDash(p.din) },
        { label: 'Bureau', value: orDash(p.creditBureau) },
        { label: 'Other boards', value: orDash(p.otherBoardsCount) },
    ];
};

const directorSubtitle = (p: ManagementProfile): string => {
    const role = (p.designation || 'Director').trim();
    const stake = orDash(p.stake);
    return stake === DASH ? role : `${role} · ${stake} stake`;
};

// "as of <month>" label under the headcount card. The API surfaces yearMonth
// as both "YYYY-MM" and "YYYYMM" depending on the source — fmtMonthYear handles
// both. We deliberately use the long form here ("Aug 2025") so it reads cleanly
// in body copy; the chart axis below uses the compact "Aug'25" form because it
// has much less horizontal room per point.
// Kept for any future "as of <month>" use (deep director cards still use it
// via fmtMonthYear). Charts moved to Overview, so the only remaining caller
// is the per-director detail page if it picks this up.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const formatAsOf = (yearMonth?: string): string | undefined => {
    if (!yearMonth) return undefined;
    return fmtMonthYear(yearMonth);
};

// Charts moved to Overview module; helper kept for symmetry / fast revival.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const buildChartPoints = (mom: Array<{ yearMonth: string; count: number }>): Array<{ label: string; value: number }> =>
    mom.slice(-18).map((x) => ({ label: x.yearMonth ?? '', value: x.count }));

// 2-up grid pairs (skill rule 2 — never use flexWrap in PDF). Empty slot
// preserves column geometry when an odd-numbered last row would otherwise
// stretch the lone card to full width.
const pairsOf = <T,>(arr: T[]): Array<[T | undefined, T | undefined]> => {
    const out: Array<[T | undefined, T | undefined]> = [];
    for (let i = 0; i < arr.length; i += 2) out.push([arr[i], arr[i + 1]]);
    return out;
};

// ════════════════════════════════════════════════════════════════
//                              P D F
// ════════════════════════════════════════════════════════════════

const PF = coverFonts.pdf;

const PdfActiveChip: React.FC<{ active: boolean }> = ({ active }) => {
    const bg = active ? overviewColors.posSoft : overviewColors.surfaceAlt;
    const fg = active ? overviewColors.pos : overviewColors.inkSubtle;
    // Three-part centering pattern (skill rule 9): lineHeight:1 + alignItems/justifyContent
    // + asymmetric padding. Without all three the chip text drifts low.
    return (
        <View
            style={{
                backgroundColor: bg,
                paddingHorizontal: mgmtDesign.chipPadX,
                paddingTop: mgmtDesign.chipPadTop,
                paddingBottom: mgmtDesign.chipPadBottom,
                borderRadius: mgmtDesign.chipRadius,
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
            }}
        >
            <Text
                style={{
                    fontSize: mgmtDesign.chipFontSize,
                    fontFamily: PF.sans,
                    fontWeight: 600,
                    color: fg,
                    letterSpacing: mgmtDesign.chipTrackingEm * mgmtDesign.chipFontSize,
                    textTransform: 'uppercase',
                    lineHeight: 1,
                }}
            >
                {active ? 'Active' : 'Inactive'}
            </Text>
        </View>
    );
};

const PdfDirectorRow: React.FC<{ row: DirectorRow; isLast: boolean }> = ({ row, isLast }) => (
    <View
        style={{
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            height: mgmtDesign.rowHeight,
            borderBottomWidth: isLast ? 0 : mgmtDesign.rowDividerWidth,
            borderBottomColor: mgmtDesign.rowSubtleColor,
        }}
    >
        {/* skill rule 9 corollary: sibling Texts at different sizes — lineHeight:1 aligns optical baselines */}
        <Text style={{ fontSize: mgmtDesign.rowLabelSize, color: overviewColors.inkMuted, fontFamily: PF.sans, lineHeight: 1 }}>
            {row.label}
        </Text>
        <Text style={{ fontSize: mgmtDesign.rowValueSize, color: overviewColors.inkSoft, fontFamily: PF.sans, lineHeight: 1 }}>
            {row.value}
        </Text>
    </View>
);

const PdfDirectorCard: React.FC<{ p: ManagementProfile }> = ({ p }) => {
    const active = p.isActive !== false;
    return (
        <View
            wrap={false}
            style={{
                backgroundColor: '#FFFFFF',
                borderWidth: mgmtDesign.cardBorder,
                borderColor: overviewColors.border,
                borderRadius: mgmtDesign.cardRadius,
                padding: mgmtDesign.cardPad,
                minHeight: mgmtDesign.cardMinHeight,
            }}
        >
            {/* Header: avatar + (name + subtitle) + status chip */}
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: mgmtDesign.headerGap }}>
                <View
                    style={{
                        width: mgmtDesign.avatarSize,
                        height: mgmtDesign.avatarSize,
                        borderRadius: mgmtDesign.avatarRadius,
                        backgroundColor: overviewColors.surfaceAlt,
                        borderWidth: mgmtDesign.cardBorder,
                        borderColor: overviewColors.border,
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                    }}
                >
                    <Text
                        style={{
                            fontSize: mgmtDesign.avatarFontSize,
                            fontFamily: PF.sans,
                            fontWeight: 600,
                            color: overviewColors.inkSoft,
                            lineHeight: 1.05,
                        }}
                    >
                        {initials(p.name)}
                    </Text>
                </View>
                <View style={{ flex: 1 }}>
                    <Text style={{ fontSize: mgmtDesign.nameSize, fontFamily: PF.sans, fontWeight: 600, color: overviewColors.ink }}>
                        {orDash(p.name)}
                    </Text>
                    <Text
                        style={{
                            fontSize: mgmtDesign.subtitleSize,
                            color: overviewColors.inkMuted,
                            fontFamily: PF.sans,
                            marginTop: mgmtDesign.subtitleGap,
                        }}
                    >
                        {directorSubtitle(p)}
                    </Text>
                </View>
                <PdfActiveChip active={active} />
            </View>

            {/* Detail rows */}
            <View style={{ marginTop: mgmtDesign.headerToRows }}>
                {buildDirectorRows(p).map((row, i, arr) => (
                    <PdfDirectorRow key={row.label} row={row} isLast={i === arr.length - 1} />
                ))}
            </View>
        </View>
    );
};

// Charts moved to Overview (below Key Competitors). The buildChartPoints
// helper is still used over there via fmtMonthYearShort, so its data path
// stays in normalizers — this module just no longer renders the chart.

const PdfManagement: React.FC<{ data: ReportData; index: string; breakBefore?: boolean }> = ({ data, index, breakBefore }) => {
    const m = data.management;
    if (!m) return null;
    // Enrich each profile with directorsDeep / relatedParties / shareholding
    // signal so the consolidated card's "Other boards" count is populated.
    // Previously the cards rendered `m.profiles` raw and `otherBoardsCount`
    // came back as undefined for most directors (it's not in the base profile
    // payload — only in directorsDeep). The single-director detail view
    // already enriched; the consolidated cards did not. Same fix on screen.
    const profiles = m.profiles.map((p) => enrichDirectorProfile(p, data));

    return (
        <PdfSection kicker="Management" title="Management Profile" index={index} breakBefore={breakBefore}>
            {profiles.length > 0 ? (
                <>
                    <Text style={SECTION_HEADING}>Directors</Text>
                    {pairsOf(profiles).map((row, ri) => (
                        <View
                            key={ri}
                            style={{
                                flexDirection: 'row',
                                gap: mgmtDesign.gridGap,
                                marginTop: ri === 0 ? 0 : mgmtDesign.gridGap,
                            }}
                            wrap={false}
                        >
                            {row.map((p, ci) =>
                                p ? (
                                    <View key={ci} style={{ flex: 1 }}>
                                        <PdfDirectorCard p={p} />
                                    </View>
                                ) : (
                                    <View key={ci} style={{ flex: 1 }} />
                                ),
                            )}
                        </View>
                    ))}
                </>
            ) : null}

            {/* Charts moved to Overview (below Key Competitors) per design feedback. */}
        </PdfSection>
    );
};

// ════════════════════════════════════════════════════════════════
//                            S C R E E N
// ════════════════════════════════════════════════════════════════

const SF = coverFonts.screen;

const ScreenActiveChip: React.FC<{ active: boolean }> = ({ active }) => {
    const bg = active ? overviewColors.posSoft : overviewColors.surfaceAlt;
    const fg = active ? overviewColors.pos : overviewColors.inkSubtle;
    return (
        <span
            style={{
                background: bg,
                color: fg,
                fontFamily: SF.sans,
                fontWeight: 600,
                fontSize: ptToPx(mgmtDesign.chipFontSize),
                letterSpacing: `${mgmtDesign.chipTrackingEm}em`,
                textTransform: 'uppercase',
                paddingLeft: ptToPx(mgmtDesign.chipPadX),
                paddingRight: ptToPx(mgmtDesign.chipPadX),
                paddingTop: ptToPx(mgmtDesign.chipPadTop),
                paddingBottom: ptToPx(mgmtDesign.chipPadBottom),
                borderRadius: ptToPx(mgmtDesign.chipRadius),
                lineHeight: 1,
                display: 'inline-flex',
                alignItems: 'center',
                flexShrink: 0,
                whiteSpace: 'nowrap',
            }}
        >
            {active ? 'Active' : 'Inactive'}
        </span>
    );
};

const ScreenDirectorRow: React.FC<{ row: DirectorRow; isLast: boolean }> = ({ row, isLast }) => (
    <div
        style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            height: ptToPx(mgmtDesign.rowHeight),
            borderBottom: isLast ? 'none' : `${ptToPx(mgmtDesign.rowDividerWidth)}px solid ${mgmtDesign.rowSubtleColor}`,
        }}
    >
        <span style={{ fontSize: ptToPx(mgmtDesign.rowLabelSize), color: overviewColors.inkMuted, fontFamily: SF.sans }}>{row.label}</span>
        <span style={{ fontSize: ptToPx(mgmtDesign.rowValueSize), color: overviewColors.inkSoft, fontFamily: SF.sans }}>{row.value}</span>
    </div>
);

const ScreenDirectorCard: React.FC<{ p: ManagementProfile }> = ({ p }) => {
    const active = p.isActive !== false;
    return (
        <div
            style={{
                background: '#FFFFFF',
                border: `${ptToPx(mgmtDesign.cardBorder)}px solid ${overviewColors.border}`,
                borderRadius: ptToPx(mgmtDesign.cardRadius),
                padding: ptToPx(mgmtDesign.cardPad),
                minHeight: ptToPx(mgmtDesign.cardMinHeight),
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <div style={{ display: 'flex', alignItems: 'center', gap: ptToPx(mgmtDesign.headerGap) }}>
                <div
                    style={{
                        width: ptToPx(mgmtDesign.avatarSize),
                        height: ptToPx(mgmtDesign.avatarSize),
                        borderRadius: '50%',
                        background: overviewColors.surfaceAlt,
                        border: `${ptToPx(mgmtDesign.cardBorder)}px solid ${overviewColors.border}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        fontSize: ptToPx(mgmtDesign.avatarFontSize),
                        fontFamily: SF.sans,
                        fontWeight: 600,
                        color: overviewColors.inkSoft,
                    }}
                >
                    {initials(p.name)}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                        style={{
                            fontSize: ptToPx(mgmtDesign.nameSize),
                            fontFamily: SF.sans,
                            fontWeight: 600,
                            color: overviewColors.ink,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {orDash(p.name)}
                    </div>
                    <div
                        style={{
                            fontSize: ptToPx(mgmtDesign.subtitleSize),
                            color: overviewColors.inkMuted,
                            fontFamily: SF.sans,
                            marginTop: ptToPx(mgmtDesign.subtitleGap),
                        }}
                    >
                        {directorSubtitle(p)}
                    </div>
                </div>
                <ScreenActiveChip active={active} />
            </div>
            <div style={{ marginTop: ptToPx(mgmtDesign.headerToRows) }}>
                {buildDirectorRows(p).map((row, i, arr) => (
                    <ScreenDirectorRow key={row.label} row={row} isLast={i === arr.length - 1} />
                ))}
            </div>
        </div>
    );
};

const ScreenManagement: React.FC<{ data: ReportData; index: string }> = ({ data, index }) => {
    const m = data.management;
    if (!m) return null;
    // Mirror of the PdfManagement enrichment so screen and PDF show the same
    // "Other boards" value on each consolidated card.
    const profiles = m.profiles.map((p) => enrichDirectorProfile(p, data));

    return (
        <ScreenSection kicker="Management" title="Management Profile" index={index}>
            {profiles.length > 0 ? (
                <>
                    <div
                        style={{
                            fontSize: ptToPx(SECTION_HEADING.fontSize),
                            color: SECTION_HEADING.color,
                            marginBottom: ptToPx(SECTION_HEADING.marginBottom),
                            fontFamily: SF.sans,
                            fontWeight: 600,
                        }}
                    >
                        Directors
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: ptToPx(mgmtDesign.gridGap) }}>
                        {pairsOf(profiles).map((row, ri) => (
                            <div key={ri} style={{ display: 'flex', gap: ptToPx(mgmtDesign.gridGap) }}>
                                {row.map((p, ci) =>
                                    p ? (
                                        <div key={ci} style={{ flex: 1, minWidth: 0 }}>
                                            <ScreenDirectorCard p={p} />
                                        </div>
                                    ) : (
                                        <div key={ci} style={{ flex: 1 }} />
                                    ),
                                )}
                            </div>
                        ))}
                    </div>
                </>
            ) : null}

            {/* Charts moved to Overview — see PdfManagement. */}
        </ScreenSection>
    );
};

// ════════════════════════════════════════════════════════════════
//                  D I R E C T O R   D E T A I L
// ════════════════════════════════════════════════════════════════
//
// Per-director full-page layout (Figma node 32:99844). Each director gets
// their own page after the Management Profile page, so the shells iterate
// over `data.management.profiles` and emit one PdfPage / ScreenPage per
// director using these components.
//
// Hero header (avatar + name + meta line + ACTIVE chip) → three info cards
// (Director Information, Regulatory & Bureau, Other Directorships). Every
// missing field is normalized to "—" via `orDash`, and number-of-rows-per-card
// is fixed so PDF and screen visually agree.

const detailDesign = {
    // Hero
    heroAvatarSize: 60,
    heroAvatarRadius: 30,
    heroAvatarFontSize: 18,
    heroGap: 14,
    heroTitleSize: 28,
    heroTitleLineHeight: 1.1,
    heroSubtitleSize: 8.5,
    heroSubtitleGap: 4,
    heroChipPadX: 7,
    heroChipPadTop: 3,
    heroChipPadBottom: 4,
    heroChipFontSize: 8,
    heroDividerColor: '#D8DADF',
    heroDividerMarginTop: 16,

    // Info cards
    cardGap: 14,
    cardPad: 14,
    cardBorder: 0.5,
    cardRadius: 4,
    cardTitleSize: 10,
    cardTitleGap: 10,
    cardRowHeight: 22,
    cardRowLabelSize: 8,
    cardRowValueSize: 8.5,
    cardRowLabelWidth: 120,
    cardRowDividerColor: '#E6E7EA',
    cardRowDividerWidth: 0.5,

    // Bureau score chip ("EXCELLENT")
    bureauChipPadX: 5.5,
    bureauChipPadTop: 1.5,
    bureauChipPadBottom: 2.5,
    bureauChipFontSize: 6.5,

    // Other directorships
    listSubheaderSize: 7.5,
    listSubheaderTrackingEm: 0.08,
    listRowHeight: 18,
    listRowFontSize: 8.5,
    listRowDateSize: 8,
    listSubheaderGap: 6,
    listGroupGap: 10,
} as const;

const formatTenureRange = (begin?: number): string => {
    if (!begin) return DASH;
    const d = new Date(begin);
    if (Number.isNaN(d.getTime())) return DASH;
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const start = `${months[d.getMonth()]} ${d.getFullYear()}`;
    const ms = Date.now() - begin;
    if (ms <= 0) return `${start} — Present`;
    const yrs = ms / (1000 * 60 * 60 * 24 * 365.25);
    return `${start} — Present (${yrs.toFixed(1)} yrs)`;
};

// Strip any existing "yrs"/"years" suffix from the raw age value before we
// re-add our own. Without this, an upstream value like "38 years" composes
// into "Age: 38 years yrs".
const formatAge = (age?: string): string | undefined => {
    if (!age) return undefined;
    const s = String(age).trim();
    if (!s) return undefined;
    const m = s.match(/(\d+)/);
    return m ? `${m[1]} yrs` : s;
};

const heroSubtitle = (p: ManagementProfile): string => {
    const parts: string[] = [];
    const age = formatAge(p.age);
    if (age) parts.push(`Age: ${age}`);
    if (p.gender) parts.push(p.gender);
    if (p.nationality) parts.push(p.nationality);
    return parts.length ? parts.join(' · ') : DASH;
};

// CIBIL band labels — matches the conventional 300–900 range buckets so we
// can show the green "EXCELLENT" / "GOOD" / etc. chip when the API gives
// us a numeric score but no pre-computed band.
const bureauScoreToBand = (score?: number): string | undefined => {
    if (typeof score !== 'number' || !Number.isFinite(score)) return undefined;
    if (score >= 800) return 'EXCELLENT';
    if (score >= 750) return 'VERY GOOD';
    if (score >= 700) return 'GOOD';
    if (score >= 650) return 'FAIR';
    return 'POOR';
};

// Normalize director / holder names for matching across the management,
// directorsDeep, shareholding and related-parties APIs. Each surface formats
// names slightly differently — strip salutations, lowercase, collapse spaces.
const normalizeDirectorName = (s?: string): string =>
    (s ?? '')
        .trim()
        .toLowerCase()
        .replace(/^(mr\.?|mrs\.?|ms\.?|dr\.?|prof\.?|shri|smt|sri)\s+/i, '')
        .replace(/\s+/g, ' ');

// ABCDE1234F → AB******4F — show first 2 + last 2, mask the middle.
// If already masked (contains '*' or 'X') or too short, return as-is.
const maskPan = (pan?: string): string | undefined => {
    if (!pan) return undefined;
    const s = pan.trim();
    if (s.length < 6 || /[*X]{3,}/.test(s)) return s;
    return s.slice(0, 2) + '*'.repeat(s.length - 4) + s.slice(-2);
};

const riskChipFromValue = (value?: string): { text: string; tone: 'LOW' | 'MEDIUM' | 'HIGH' } | undefined => {
    const v = (value ?? '').trim().toLowerCase();
    if (v === 'clear' || v === 'no' || v === 'no hits') return { text: 'Clear', tone: 'LOW' };
    if (v === 'hit' || v === 'yes') return { text: value!.trim(), tone: 'HIGH' };
    if (v === 'watch') return { text: 'Watch', tone: 'MEDIUM' };
    return undefined;
};

// Merge directorship lists from two sources. directorsDeep contributes the
// `name + since/from/to` date fields; relatedParties contributes the Tier 3-A
// metadata (designation, risk, alertsCount). Same board appearing in both
// sources is keyed by normalised name so we don't double-list it. Each field
// is filled from whichever source first reported it; deep wins ties.
const mergePresentBoards = (
    deepBoards: DirectorshipBoard[] | undefined,
    relatedBoards: DirectorshipBoard[] | undefined,
): DirectorshipBoard[] | undefined => {
    const deep = deepBoards ?? [];
    const related = relatedBoards ?? [];
    if (deep.length === 0 && related.length === 0) return undefined;
    const byKey = new Map<string, DirectorshipBoard>();
    deep.forEach((b) => {
        if (!b.name) return;
        byKey.set(normalizeDirectorName(b.name), { ...b });
    });
    related.forEach((b) => {
        if (!b.name) return;
        const key = normalizeDirectorName(b.name);
        const existing = byKey.get(key);
        if (existing) {
            existing.designation = existing.designation ?? b.designation;
            existing.risk = existing.risk ?? b.risk;
            existing.alertsCount = existing.alertsCount ?? b.alertsCount;
        } else {
            byKey.set(key, { ...b });
        }
    });
    return Array.from(byKey.values());
};

// Merge the basic ManagementProfile with whatever extra signal we have in the
// other normalized data sources. Layered fallbacks (existing field → directorsDeep
// → relatedParties → shareholding) so we surface as much real data as the API
// has returned. Anything still missing renders as "—".
//
// IMPORTANT: in `directorsDeep` the field named `creditBureau` is the BUREAU NAME
// (e.g. "EQUIFAX"), not the score. The numeric score lives in `creditScore`.
// Always prefer `creditScore` for the value column and derive the band from it.
const enrichDirectorProfile = (profile: ManagementProfile, data: ReportData): ManagementProfile => {
    const key = normalizeDirectorName(profile.name);

    const deep: DirectorDeepProfile | undefined = data.directorsDeep?.profiles.find((d) => normalizeDirectorName(d.name) === key);

    const relatedDir = data.relatedParties?.directors?.find((r) => normalizeDirectorName(r.name) === key);

    const holder: ShareholdingRow | undefined = data.shareholding?.rows.find((r) => normalizeDirectorName(r.holderName) === key);

    // Director Bureau — per-bureau rows (CIBIL / Equifax / Experian).
    // Prefer CIBIL score; fall back to the highest score from any bureau.
    const bureauDir = data.directorBureau?.directors?.find((d) => normalizeDirectorName(d.name) === key);
    const cibilRow = bureauDir?.rows?.find((r) => r.bureau === 'CIBIL');
    const bestBureauRow = cibilRow ?? bureauDir?.rows?.find((r) => r.score != null && Number.isFinite(r.score));

    // AML/PEP entity match — only 'individual' type entities are directors.
    const amlEntity = data.legalRisk?.amlPepEntities?.find((e) => e.type === 'individual' && normalizeDirectorName(e.name) === key);
    const pepDim = amlEntity?.dimensions?.find((d) => /pep/i.test(d.label));
    const sanctionsDim = amlEntity?.dimensions?.find((d) => /sanction|watchlist/i.test(d.label));
    const adverseDim = amlEntity?.dimensions?.find((d) => /adverse|media|press/i.test(d.label));
    const dimLabel = (dim?: { status: string }): string | undefined => {
        if (!dim) return undefined;
        if (dim.status === 'clear') return 'Clear';
        if (dim.status === 'hit') return 'Hit';
        if (dim.status === 'watch') return 'Watch';
        return undefined;
    };

    // Bureau: numeric score wins. Bureau name (e.g. "EQUIFAX") is the lowest
    // fallback — better to show a name than nothing, but we'd rather show "812".
    const bureauValue =
        profile.creditBureau ??
        (deep?.creditScore != null ? String(deep.creditScore) : undefined) ??
        deep?.creditBureau ??
        (bestBureauRow?.score != null ? String(bestBureauRow.score) : undefined);
    const bureauLabel = profile.bureauScoreLabel ?? bureauScoreToBand(deep?.creditScore) ?? bureauScoreToBand(bestBureauRow?.score);

    // Other directorships — three layered sources:
    //   1. directorsDeep current/former entities (richest: name + dates)
    //   2. relatedParties.directors[].otherEntities (just names — what
    //      Structure & Relations renders today)
    // Self-references filtered (a director's own company isn't an
    // "other directorship").
    const ownOrgKey = normalizeDirectorName(data.identity?.orgName);

    const presentFromDeep: DirectorshipBoard[] | undefined = deep?.currentEntities
        ?.filter((e) => e.name && normalizeDirectorName(e.name) !== ownOrgKey)
        .map((e) => ({ name: e.name, since: e.beginDate }));

    const pastFromDeep: DirectorshipBoard[] | undefined = deep?.formerEntities
        ?.filter((e) => e.name && normalizeDirectorName(e.name) !== ownOrgKey)
        .map((e) => ({ name: e.name, from: e.beginDate, to: e.endDate }));

    // Tier 3-A: prefer the rich `otherBoards` shape (designation + risk +
    // alerts count) when the related-parties normalizer produced it; fall
    // back to the legacy `otherEntities` name-only list otherwise.
    const presentFromRelated: DirectorshipBoard[] | undefined =
        relatedDir?.otherBoards && relatedDir.otherBoards.length
            ? relatedDir.otherBoards
                  .filter((b) => b.name && normalizeDirectorName(b.name) !== ownOrgKey)
                  .map((b) => ({
                      name: b.name,
                      designation: b.designation,
                      risk: b.risk,
                      alertsCount: b.alertsCount,
                  }))
            : relatedDir?.otherEntities?.filter((name) => name && normalizeDirectorName(name) !== ownOrgKey).map((name) => ({ name }));

    // Merge deep (rich dates) + related (designation + risk + alertsCount) by
    // name. Without this, the layered fallback would drop the Tier 3-A
    // metadata whenever directorsDeep returned even a single board, and the
    // directorships card would lose its risk chips. Deep wins on `name`,
    // `since`, `from`, `to`; related fills in everything else.
    const merged = mergePresentBoards(presentFromDeep, presentFromRelated);
    const present = profile.presentBoards?.length ? profile.presentBoards : merged ?? undefined;
    const past = profile.pastBoards?.length ? profile.pastBoards : pastFromDeep?.length ? pastFromDeep : undefined;

    return {
        ...profile,
        creditBureau: bureauValue,
        bureauScoreLabel: bureauLabel,
        legalCasesAgainst: profile.legalCasesAgainst ?? deep?.legalCasesCount,
        pan: maskPan(profile.pan) ?? maskPan(deep?.pan),
        gender: profile.gender ?? deep?.gender,
        pepStatus: profile.pepStatus ?? dimLabel(pepDim),
        sanctions: profile.sanctions ?? dimLabel(sanctionsDim),
        adverseMedia: profile.adverseMedia ?? dimLabel(adverseDim),
        stake: profile.stake ?? (holder ? `${holder.percent}%` : undefined),
        presentBoards: present,
        pastBoards: past,
        otherBoardsCount: (present?.length ?? 0) + (past?.length ?? 0) || profile.otherBoardsCount,
        phone: profile.phone ?? deep?.phone,
        dpdAccounts: profile.dpdAccounts ?? deep?.dpdAccounts ?? bestBureauRow?.dpdAccounts,
        totalAccounts: profile.totalAccounts ?? deep?.totalAccounts,
        currentOverdue: profile.currentOverdue ?? deep?.currentOverdue ?? bestBureauRow?.currentOverdue,
        maxDpds: profile.maxDpds ?? deep?.maxDpds ?? bestBureauRow?.maxDpds,
        enquiriesInPast90Days: profile.enquiriesInPast90Days ?? deep?.enquiriesInPast90Days,
        // Tier 2A: credit-behavior trend signals. Layered fallback to existing
        // profile fields so any upstream enrichment still wins; everything else
        // comes from the directorsDeep overview list.
        enquirySummary: profile.enquirySummary ?? deep?.enquirySummary,
        last12MonthsDpsInstances: profile.last12MonthsDpsInstances ?? deep?.last12MonthsDpsInstances,
        before12MonthsDpsInstances: profile.before12MonthsDpsInstances ?? deep?.before12MonthsDpsInstances,
        last12MonthsDpsAccounts: profile.last12MonthsDpsAccounts ?? deep?.last12MonthsDpsAccounts,
        before12MonthsDpsAccounts: profile.before12MonthsDpsAccounts ?? deep?.before12MonthsDpsAccounts,
        // Tier 2C: bureau staleness + failure state.
        bureauLastFetched: profile.bureauLastFetched ?? deep?.lastFetched,
        bureauError: profile.bureauError ?? deep?.bureauError,
    };
};

type InfoRow =
    | { kind: 'text'; label: string; value: string }
    | { kind: 'chip'; label: string; chip: { text: string; tone: 'pos' | 'neutral' } }
    | { kind: 'scoreChip'; label: string; value: string; chip?: { text: string } }
    | { kind: 'riskChip'; label: string; chip: { text: string; tone: 'LOW' | 'MEDIUM' | 'HIGH' } };

const buildDirectorInfoRows = (p: ManagementProfile): InfoRow[] => [
    {
        kind: 'chip',
        label: 'Status',
        chip: { text: p.isActive === false ? 'Inactive' : 'Active', tone: p.isActive === false ? 'neutral' : 'pos' },
    },
    { kind: 'text', label: 'Designation', value: orDash(p.designation) },
    { kind: 'text', label: 'Tenure', value: formatTenureRange(p.tenureBeginDate) },
    { kind: 'text', label: 'DIN No.', value: orDash(p.din) },
    { kind: 'text', label: 'PAN', value: orDash(p.pan) },
];

// When `hasAmlMatrix` is true, the dedicated Regulatory Screening card is
// rendering the full 9-dimension matrix — so we drop the 3 summary rows
// (PEP / Sanctions / Adverse media) from this card to avoid duplication.
const buildBureauRows = (p: ManagementProfile, opts?: { hasAmlMatrix?: boolean }): InfoRow[] => {
    const rows: InfoRow[] = [
        {
            kind: 'scoreChip',
            label: 'Bureau score (CIBIL)',
            value: orDash(p.creditBureau),
            chip: p.bureauScoreLabel ? { text: p.bureauScoreLabel.toUpperCase() } : undefined,
        },
        { kind: 'text', label: 'Legal cases (against)', value: orDash(p.legalCasesAgainst) },
        { kind: 'text', label: 'Legal cases (filed by)', value: orDash(p.legalCasesFiledBy) },
    ];
    if (!opts?.hasAmlMatrix) {
        const pepChip = riskChipFromValue(p.pepStatus);
        if (pepChip) rows.push({ kind: 'riskChip', label: 'PEP status', chip: pepChip });
        else rows.push({ kind: 'text', label: 'PEP status', value: orDash(p.pepStatus) });

        const sanctionsChip = riskChipFromValue(p.sanctions);
        if (sanctionsChip) rows.push({ kind: 'riskChip', label: 'Sanctions / watchlists', chip: sanctionsChip });
        else rows.push({ kind: 'text', label: 'Sanctions / watchlists', value: orDash(p.sanctions) });

        const adverseChip = riskChipFromValue(p.adverseMedia);
        if (adverseChip) rows.push({ kind: 'riskChip', label: 'Adverse media (24m)', chip: adverseChip });
        else rows.push({ kind: 'text', label: 'Adverse media (24m)', value: orDash(p.adverseMedia) });
    }
    // CIBIL behavior signal — only render rows that actually have data so we don't
    // explode the card height with seven extra "—" lines when none of them lit up.
    const totalsLabel =
        p.dpdAccounts != null && p.totalAccounts != null
            ? `${p.dpdAccounts} of ${p.totalAccounts}`
            : p.dpdAccounts != null
            ? String(p.dpdAccounts)
            : p.totalAccounts != null
            ? `0 of ${p.totalAccounts}`
            : null;
    if (totalsLabel != null) rows.push({ kind: 'text', label: 'Accounts with DPD', value: totalsLabel });
    if (p.maxDpds != null) rows.push({ kind: 'text', label: 'Max DPDs (days)', value: String(p.maxDpds) });
    if (p.currentOverdue != null) rows.push({ kind: 'text', label: 'Current overdue', value: String(p.currentOverdue) });
    if (p.enquiriesInPast90Days != null) rows.push({ kind: 'text', label: 'Enquiries (90d)', value: String(p.enquiriesInPast90Days) });
    if (p.phone) rows.push({ kind: 'text', label: 'Phone', value: orDash(p.phone) });
    // Bureau metadata footer — staleness + failure state. We render these
    // close to the bottom of the card so the score itself reads first, with
    // freshness/error context immediately under it. If a bureau call failed,
    // the score will be blank ("—") above and the error row explains why.
    if (p.bureauError) {
        rows.push({ kind: 'text', label: 'Bureau status', value: `Failed · ${truncate(p.bureauError, 60)}` });
    } else if (p.bureauLastFetched != null) {
        rows.push({ kind: 'text', label: 'Bureau refreshed', value: fmtDate(p.bureauLastFetched) });
    }
    return rows;
};

// ───────────────── PDF director detail ─────────────────

// `full=true` renders the long form ("Active Director") used in the page hero;
// `full=false` renders just "Active"/"Inactive" used in the Director Information
// card's Status row, where the surrounding label already says "Status".
const PdfHeroChip: React.FC<{ active: boolean; full?: boolean }> = ({ active, full = true }) => {
    const bg = active ? overviewColors.posSoft : overviewColors.surfaceAlt;
    const fg = active ? overviewColors.pos : overviewColors.inkSubtle;
    const label = full ? (active ? 'Active Director' : 'Inactive Director') : active ? 'Active' : 'Inactive';
    return (
        <View
            style={{
                backgroundColor: bg,
                paddingHorizontal: detailDesign.heroChipPadX,
                paddingTop: detailDesign.heroChipPadTop,
                paddingBottom: detailDesign.heroChipPadBottom,
                borderRadius: 2,
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
            }}
        >
            <Text
                style={{
                    fontSize: detailDesign.heroChipFontSize,
                    fontFamily: PF.sans,
                    fontWeight: 600,
                    color: fg,
                    letterSpacing: 0.06 * detailDesign.heroChipFontSize,
                    textTransform: 'uppercase',
                    lineHeight: 1,
                }}
            >
                {label}
            </Text>
        </View>
    );
};

const PdfBureauChip: React.FC<{ text: string }> = ({ text }) => (
    <View
        style={{
            backgroundColor: overviewColors.posSoft,
            paddingHorizontal: detailDesign.bureauChipPadX,
            paddingTop: detailDesign.bureauChipPadTop,
            paddingBottom: detailDesign.bureauChipPadBottom,
            borderRadius: 1.5,
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
        }}
    >
        <Text
            style={{
                fontSize: detailDesign.bureauChipFontSize,
                fontFamily: PF.sans,
                fontWeight: 600,
                color: overviewColors.pos,
                letterSpacing: 0.08 * detailDesign.bureauChipFontSize,
                textTransform: 'uppercase',
                lineHeight: 1,
            }}
        >
            {text}
        </Text>
    </View>
);

const PdfInfoRow: React.FC<{ row: InfoRow; isLast: boolean }> = ({ row, isLast }) => {
    const baseStyle = {
        flexDirection: 'row' as const,
        alignItems: 'center' as const,
        height: detailDesign.cardRowHeight,
        borderBottomWidth: isLast ? 0 : detailDesign.cardRowDividerWidth,
        borderBottomColor: detailDesign.cardRowDividerColor,
    };
    // wrap=false on each row so a row never splits mid-cell. The PARENT card
    // is wrap-eligible (see PdfInfoCard) so a long card can break between
    // rows when its cumulative height exceeds the page. This combination
    // prevents yoga from squishing rows when all three cards (Info + Bureau
    // + Directorships) collectively overflow the page height — the failure
    // mode that produced collapsed/overlapping rows on data-heavy directors.
    return (
        <View style={baseStyle} wrap={false}>
            <Text
                style={{
                    width: detailDesign.cardRowLabelWidth,
                    fontSize: detailDesign.cardRowLabelSize,
                    color: overviewColors.inkMuted,
                    fontFamily: PF.sans,
                    lineHeight: 1,
                }}
            >
                {row.label}
            </Text>
            {/* Right-align values like the Other Directorships card. `flex: 1`
                + `justifyContent: 'flex-end'` pushes the value (and any
                adjacent chip) to the right edge of the row. */}
            <View style={{ flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-end', gap: 8 }}>
                {row.kind === 'chip' ? (
                    <PdfHeroChip active={row.chip.tone === 'pos'} full={false} />
                ) : row.kind === 'scoreChip' ? (
                    <>
                        <Text
                            style={{
                                fontSize: detailDesign.cardRowValueSize,
                                fontFamily: PF.mono,
                                fontWeight: 500,
                                color: overviewColors.pos,
                                lineHeight: 1,
                            }}
                        >
                            {row.value}
                        </Text>
                        {row.chip ? <PdfBureauChip text={row.chip.text} /> : null}
                    </>
                ) : row.kind === 'riskChip' ? (
                    <PdfChip tone={row.chip.tone}>{row.chip.text}</PdfChip>
                ) : (
                    <Text
                        style={{ fontSize: detailDesign.cardRowValueSize, color: overviewColors.ink, fontFamily: PF.sans, lineHeight: 1 }}
                    >
                        {row.value}
                    </Text>
                )}
            </View>
        </View>
    );
};

const PdfInfoCard: React.FC<{ title: string; rows: InfoRow[] }> = ({ title, rows }) => (
    // wrap-eligible: a long bureau card (with 10+ data-rich rows) can break
    // naturally between rows when it doesn't fit on the page. Earlier this
    // was wrap=false; combined with the other wrap=false cards on the same
    // page, react-pdf squished all rows down to zero height when their
    // cumulative height exceeded the page (visible on data-heavy directors
    // like Meleveetil Damodaran). Each PdfInfoRow is wrap=false on its own
    // so individual rows still never split.
    <View
        style={{
            borderWidth: detailDesign.cardBorder,
            borderColor: overviewColors.border,
            borderRadius: detailDesign.cardRadius,
            padding: detailDesign.cardPad,
            backgroundColor: '#FFFFFF',
        }}
    >
        <Text
            style={{
                fontSize: detailDesign.cardTitleSize,
                fontFamily: PF.sans,
                fontWeight: 600,
                color: overviewColors.ink,
                marginBottom: detailDesign.cardTitleGap,
            }}
        >
            {title}
        </Text>
        {rows.map((row, i) => (
            <PdfInfoRow key={`${row.label}-${i}`} row={row} isLast={i === rows.length - 1} />
        ))}
    </View>
);

// Per PM feedback: dropped the Present/Past sub-section split. The card is
// now a single flat list with an Active/Past chip per row in place of the
// date column. Chip carries the same signal more compactly and the row
// rhythm stays consistent between mixed-status profiles.
//
// Tier 3-A: each row carries optional designation + risk + alerts count
// sourced from relatedParties. Empty fields render nothing (no "—" fillers).
// Tier 3-C: rows are ordered most-recent-first within each section so the
// underwriter sees the director's latest activity at the top.
type BoardRow = {
    name: string;
    status: 'Active' | 'Inactive';
};

// Parse a permissive directorship date — Polus may send a 4-digit year ("2010"),
// a "Mon YYYY" string ("Jul 2023"), or an ISO/epoch number. Returns a sortable
// numeric timestamp; undefined when nothing's there. We don't try to be exact —
// month-precision is enough since the underwriter sees the year prominently.
const parseBoardDate = (raw?: string): number | undefined => {
    if (!raw) return undefined;
    const s = String(raw).trim();
    if (!s) return undefined;
    // 4-digit year alone → mid-year of that year
    if (/^\d{4}$/.test(s)) return Date.UTC(Number(s), 6, 1);
    // "Mon YYYY" → first of that month
    const months = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
    const m = s.match(/^([A-Za-z]{3,9})\s+(\d{4})$/);
    if (m) {
        const monIdx = months.indexOf(m[1].slice(0, 3).toLowerCase());
        if (monIdx >= 0) return Date.UTC(Number(m[2]), monIdx, 1);
    }
    const ts = Date.parse(s);
    return Number.isFinite(ts) ? ts : undefined;
};

const buildBoardRows = (present: DirectorshipBoard[], past: DirectorshipBoard[]): BoardRow[] => {
    // Sort each section in-place: present by `since` descending (most recent
    // first), past by `from` descending. Boards without a parseable date sink
    // to the bottom of their section so the chronological signal stays clean.
    const sortedPresent = [...present].sort((a, b) => (parseBoardDate(b.since) ?? -Infinity) - (parseBoardDate(a.since) ?? -Infinity));
    const sortedPast = [...past].sort((a, b) => (parseBoardDate(b.from) ?? -Infinity) - (parseBoardDate(a.from) ?? -Infinity));
    return [
        ...sortedPresent.map((b) => ({ name: orDash(b.name), status: 'Active' as const })),
        ...sortedPast.map((b) => ({ name: orDash(b.name), status: 'Inactive' as const })),
    ];
};

const PdfBoardListRow: React.FC<{ row: BoardRow; isLast: boolean }> = ({ row, isLast }) => (
    // wrap=false per row: the parent card is wrap-eligible (long lists break
    // between rows). Atomic rows + wrap-eligible parent = the safe-pagination
    // pattern (mirrors PdfDataTable / RelatedParties.TableRow).
    <View
        wrap={false}
        style={{
            flexDirection: 'row',
            alignItems: 'center',
            gap: 6,
            paddingVertical: 5,
            minHeight: detailDesign.listRowHeight,
            borderBottomWidth: isLast ? 0 : detailDesign.cardRowDividerWidth,
            borderBottomColor: detailDesign.cardRowDividerColor,
        }}
    >
        {/* skill rule 9 corollary: sibling label of a chip needs lineHeight:1 to match the chip's tight line-box */}
        <Text
            style={{
                fontSize: detailDesign.listRowFontSize,
                color: overviewColors.ink,
                fontFamily: PF.sans,
                flex: 1,
                paddingRight: 4,
                lineHeight: 1.2,
            }}
        >
            {row.name}
        </Text>
        <PdfChip tone={row.status === 'Active' ? 'info' : 'muted'}>{row.status}</PdfChip>
    </View>
);

const PdfDirectorshipsCard: React.FC<{ present: DirectorshipBoard[]; past: DirectorshipBoard[] }> = ({ present, past }) => {
    const rows = buildBoardRows(present, past);
    return (
        // wrap-eligible card. Some directors have 30+ board seats — a wrap=false
        // card that tall doesn't fit on any page, and combined with the other
        // wrap=false cards on the same director-detail page would force yoga
        // to squish all rows to zero height. Rows are individually wrap=false
        // so a single row never splits mid-cell.
        <View
            style={{
                borderWidth: detailDesign.cardBorder,
                borderColor: overviewColors.border,
                borderRadius: detailDesign.cardRadius,
                padding: detailDesign.cardPad,
                backgroundColor: '#FFFFFF',
            }}
        >
            <Text
                style={{
                    fontSize: detailDesign.cardTitleSize,
                    fontFamily: PF.sans,
                    fontWeight: 600,
                    color: overviewColors.ink,
                    marginBottom: detailDesign.cardTitleGap,
                }}
            >
                Other Directorships
            </Text>
            {rows.length === 0 ? (
                <Text style={{ fontSize: detailDesign.listRowFontSize, color: overviewColors.inkMuted, fontFamily: PF.sans }}>{DASH}</Text>
            ) : (
                rows.map((r, i) => <PdfBoardListRow key={`${r.name}-${i}`} row={r} isLast={i === rows.length - 1} />)
            )}
        </View>
    );
};

export type PdfDirectorDetailProps = {
    profile: ManagementProfile;
    moduleIndex: string; // "05" — TOC index of the management module
    directorIndex: number; // 1-based position
    directorTotal: number;
    // Full ReportData so we can fill bureau / boards / stake from
    // directorsDeep + shareholding when the management API doesn't carry them.
    data: ReportData;
};

export const PdfDirectorDetail: React.FC<PdfDirectorDetailProps> = ({
    profile: rawProfile,
    moduleIndex,
    directorIndex,
    directorTotal,
    data,
}) => {
    const profile = enrichDirectorProfile(rawProfile, data);
    const active = profile.isActive !== false;
    const present = profile.presentBoards ?? [];
    const past = profile.pastBoards ?? [];
    return (
        <View>
            {/* Hero — eyebrow text removed; the page chrome already shows the section
                index and the running header carries the org name, so the on-page
                "Section X · Director Y of Z" was duplicate signal. */}
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: detailDesign.heroGap }}>
                <View
                    style={{
                        width: detailDesign.heroAvatarSize,
                        height: detailDesign.heroAvatarSize,
                        borderRadius: detailDesign.heroAvatarRadius,
                        backgroundColor: overviewColors.surfaceAlt,
                        borderWidth: detailDesign.cardBorder,
                        borderColor: overviewColors.border,
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                    }}
                >
                    <Text
                        style={{
                            fontSize: detailDesign.heroAvatarFontSize,
                            fontFamily: PF.sans,
                            fontWeight: 600,
                            color: overviewColors.inkSoft,
                            lineHeight: 1.05,
                        }}
                    >
                        {initials(profile.name)}
                    </Text>
                </View>
                <View style={{ flex: 1 }}>
                    <Text
                        style={{
                            fontSize: detailDesign.heroTitleSize,
                            fontFamily: PF.display,
                            color: overviewColors.ink,
                            lineHeight: detailDesign.heroTitleLineHeight,
                        }}
                    >
                        {orDash(profile.name)}
                    </Text>
                    <Text
                        style={{
                            fontSize: detailDesign.heroSubtitleSize,
                            fontFamily: PF.mono,
                            color: overviewColors.inkMuted,
                            marginTop: detailDesign.heroSubtitleGap,
                        }}
                    >
                        {heroSubtitle(profile)}
                    </Text>
                </View>
                <PdfHeroChip active={active} />
            </View>

            {/* Hairline divider */}
            <View
                style={{
                    height: 0.5,
                    backgroundColor: detailDesign.heroDividerColor,
                    marginTop: detailDesign.heroDividerMarginTop,
                    marginBottom: detailDesign.cardGap,
                }}
            />

            {/* Cards */}
            <View style={{ gap: detailDesign.cardGap }}>
                <PdfInfoCard title="Director Information" rows={buildDirectorInfoRows(profile)} />
                <PdfInfoCard title="Regulatory & Bureau" rows={buildBureauRows(profile)} />
                <PdfDirectorshipsCard present={present} past={past} />
            </View>
        </View>
    );
};

// ───────────────── Screen director detail ─────────────────

const ScreenHeroChip: React.FC<{ active: boolean; full?: boolean }> = ({ active, full = true }) => {
    const bg = active ? overviewColors.posSoft : overviewColors.surfaceAlt;
    const fg = active ? overviewColors.pos : overviewColors.inkSubtle;
    const label = full ? (active ? 'Active Director' : 'Inactive Director') : active ? 'Active' : 'Inactive';
    return (
        <span
            style={{
                background: bg,
                color: fg,
                fontFamily: SF.sans,
                fontWeight: 600,
                fontSize: ptToPx(detailDesign.heroChipFontSize),
                letterSpacing: `${0.06}em`,
                textTransform: 'uppercase',
                paddingLeft: ptToPx(detailDesign.heroChipPadX),
                paddingRight: ptToPx(detailDesign.heroChipPadX),
                paddingTop: ptToPx(detailDesign.heroChipPadTop),
                paddingBottom: ptToPx(detailDesign.heroChipPadBottom),
                borderRadius: 3,
                lineHeight: 1,
                display: 'inline-flex',
                alignItems: 'center',
                whiteSpace: 'nowrap',
                flexShrink: 0,
            }}
        >
            {label}
        </span>
    );
};

const ScreenBureauChip: React.FC<{ text: string }> = ({ text }) => (
    <span
        style={{
            background: overviewColors.posSoft,
            color: overviewColors.pos,
            fontFamily: SF.sans,
            fontWeight: 600,
            fontSize: ptToPx(detailDesign.bureauChipFontSize),
            letterSpacing: `${0.08}em`,
            textTransform: 'uppercase',
            paddingLeft: ptToPx(detailDesign.bureauChipPadX),
            paddingRight: ptToPx(detailDesign.bureauChipPadX),
            paddingTop: ptToPx(detailDesign.bureauChipPadTop),
            paddingBottom: ptToPx(detailDesign.bureauChipPadBottom),
            borderRadius: 2,
            lineHeight: 1,
            display: 'inline-flex',
            alignItems: 'center',
            whiteSpace: 'nowrap',
            flexShrink: 0,
        }}
    >
        {text}
    </span>
);

const ScreenInfoRow: React.FC<{ row: InfoRow; isLast: boolean }> = ({ row, isLast }) => (
    <div
        style={{
            display: 'flex',
            alignItems: 'center',
            height: ptToPx(detailDesign.cardRowHeight),
            borderBottom: isLast ? 'none' : `${ptToPx(detailDesign.cardRowDividerWidth)}px solid ${detailDesign.cardRowDividerColor}`,
        }}
    >
        <span
            style={{
                width: ptToPx(detailDesign.cardRowLabelWidth),
                fontSize: ptToPx(detailDesign.cardRowLabelSize),
                color: overviewColors.inkMuted,
                fontFamily: SF.sans,
                flexShrink: 0,
            }}
        >
            {row.label}
        </span>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8 }}>
            {row.kind === 'chip' ? (
                <ScreenHeroChip active={row.chip.tone === 'pos'} full={false} />
            ) : row.kind === 'scoreChip' ? (
                <>
                    <span
                        style={{
                            fontSize: ptToPx(detailDesign.cardRowValueSize),
                            fontFamily: SF.mono,
                            fontWeight: 500,
                            color: overviewColors.pos,
                        }}
                    >
                        {row.value}
                    </span>
                    {row.chip ? <ScreenBureauChip text={row.chip.text} /> : null}
                </>
            ) : row.kind === 'riskChip' ? (
                <ScreenChip tone={row.chip.tone}>{row.chip.text}</ScreenChip>
            ) : (
                <span style={{ fontSize: ptToPx(detailDesign.cardRowValueSize), color: overviewColors.ink, fontFamily: SF.sans }}>
                    {row.value}
                </span>
            )}
        </div>
    </div>
);

const ScreenInfoCard: React.FC<{ title: string; rows: InfoRow[] }> = ({ title, rows }) => (
    <div
        style={{
            border: `${ptToPx(detailDesign.cardBorder)}px solid ${overviewColors.border}`,
            borderRadius: ptToPx(detailDesign.cardRadius),
            padding: ptToPx(detailDesign.cardPad),
            background: '#FFFFFF',
        }}
    >
        <div
            style={{
                fontSize: ptToPx(detailDesign.cardTitleSize),
                fontFamily: SF.sans,
                fontWeight: 600,
                color: overviewColors.ink,
                marginBottom: ptToPx(detailDesign.cardTitleGap),
            }}
        >
            {title}
        </div>
        {rows.map((row, i) => (
            <ScreenInfoRow key={`${row.label}-${i}`} row={row} isLast={i === rows.length - 1} />
        ))}
    </div>
);

const ScreenDirectorshipsCard: React.FC<{ present: DirectorshipBoard[]; past: DirectorshipBoard[] }> = ({ present, past }) => {
    const rows = buildBoardRows(present, past);
    return (
        <div
            style={{
                border: `${ptToPx(detailDesign.cardBorder)}px solid ${overviewColors.border}`,
                borderRadius: ptToPx(detailDesign.cardRadius),
                padding: ptToPx(detailDesign.cardPad),
                background: '#FFFFFF',
            }}
        >
            <div
                style={{
                    fontSize: ptToPx(detailDesign.cardTitleSize),
                    fontFamily: SF.sans,
                    fontWeight: 600,
                    color: overviewColors.ink,
                    marginBottom: ptToPx(detailDesign.cardTitleGap),
                }}
            >
                Other Directorships
            </div>
            {rows.length === 0 ? (
                <div style={{ fontSize: ptToPx(detailDesign.listRowFontSize), color: overviewColors.inkMuted, fontFamily: SF.sans }}>
                    {DASH}
                </div>
            ) : (
                rows.map((r, i) => (
                    <div
                        key={`${r.name}-${i}`}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6,
                            padding: '6px 0',
                            borderBottom:
                                i === rows.length - 1
                                    ? 'none'
                                    : `${ptToPx(detailDesign.cardRowDividerWidth)}px solid ${detailDesign.cardRowDividerColor}`,
                        }}
                    >
                        <span
                            style={{
                                fontSize: ptToPx(detailDesign.listRowFontSize),
                                color: overviewColors.ink,
                                fontFamily: SF.sans,
                                flex: 1,
                                paddingRight: 4,
                                lineHeight: 1.2,
                            }}
                        >
                            {r.name}
                        </span>
                        <ScreenChip tone={r.status === 'Active' ? 'info' : 'muted'}>{r.status}</ScreenChip>
                    </div>
                ))
            )}
        </div>
    );
};

export type ScreenDirectorDetailProps = PdfDirectorDetailProps;

export const ScreenDirectorDetail: React.FC<ScreenDirectorDetailProps> = ({
    profile: rawProfile,
    moduleIndex,
    directorIndex,
    directorTotal,
    data,
}) => {
    const profile = enrichDirectorProfile(rawProfile, data);
    const active = profile.isActive !== false;
    const present = profile.presentBoards ?? [];
    const past = profile.pastBoards ?? [];
    return (
        <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: ptToPx(detailDesign.heroGap) }}>
                <div
                    style={{
                        width: ptToPx(detailDesign.heroAvatarSize),
                        height: ptToPx(detailDesign.heroAvatarSize),
                        borderRadius: '50%',
                        background: overviewColors.surfaceAlt,
                        border: `${ptToPx(detailDesign.cardBorder)}px solid ${overviewColors.border}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        fontSize: ptToPx(detailDesign.heroAvatarFontSize),
                        fontFamily: SF.sans,
                        fontWeight: 600,
                        color: overviewColors.inkSoft,
                    }}
                >
                    {initials(profile.name)}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                        style={{
                            fontSize: ptToPx(detailDesign.heroTitleSize),
                            fontFamily: SF.display,
                            color: overviewColors.ink,
                            lineHeight: detailDesign.heroTitleLineHeight,
                        }}
                    >
                        {orDash(profile.name)}
                    </div>
                    <div
                        style={{
                            fontSize: ptToPx(detailDesign.heroSubtitleSize),
                            fontFamily: SF.mono,
                            color: overviewColors.inkMuted,
                            marginTop: ptToPx(detailDesign.heroSubtitleGap),
                        }}
                    >
                        {heroSubtitle(profile)}
                    </div>
                </div>
                <ScreenHeroChip active={active} />
            </div>
            <div
                style={{
                    height: 0.5,
                    background: detailDesign.heroDividerColor,
                    marginTop: ptToPx(detailDesign.heroDividerMarginTop),
                    marginBottom: ptToPx(detailDesign.cardGap),
                }}
            />
            <div style={{ display: 'flex', flexDirection: 'column', gap: ptToPx(detailDesign.cardGap) }}>
                <ScreenInfoCard title="Director Information" rows={buildDirectorInfoRows(profile)} />
                <ScreenInfoCard title="Regulatory & Bureau" rows={buildBureauRows(profile)} />
                <ScreenDirectorshipsCard present={present} past={past} />
            </div>
        </div>
    );
};

// ════════════════════════════════════════════════════════════════
//                          M O D U L E   D E F
// ════════════════════════════════════════════════════════════════

export const managementModule: ModuleDefinition = {
    meta: {
        id: 'management',
        title: 'Management Profile',
        description: 'Director profiles and EPF-based headcount trend.',
        dataSource: 'Profile Overview (management + employee)',
    },
    hasData: (d) => !!d.management && (d.management.profiles.length > 0 || d.management.momEmployee.length > 0),
    Pdf: PdfManagement,
    Screen: ScreenManagement,
};
